import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { MailCheck } from "lucide-react";

export default function RegisterPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ prenom: "", nom: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [resent, setResent] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleResend = async () => {
    try {
      const { data } = await api.post("/auth/resend-verification", { email: form.email });
      setResent(data.message);
    } catch {
      setResent("Une erreur est survenue. Réessaie dans quelques instants.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/register", form);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err, "Inscription impossible."));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-md border-slate-200 shadow-sm text-center">
          <CardContent className="pt-8 pb-8 space-y-4">
            <MailCheck className="w-12 h-12 text-teal-600 mx-auto" />
            <h2 className="font-heading text-xl font-semibold text-slate-900" data-testid="register-success-title">
              Vérifie ta boite mail
            </h2>
            <p className="text-slate-600 text-sm" data-testid="register-success-message">
              Un lien de vérification vient d'être envoyé à <strong>{form.email}</strong>. Il est valable 24 heures.
              Tant que ton email n'est pas vérifié, ton compte reste inactif et sans crédits.
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={() => navigate("/connexion")} className="bg-teal-600 hover:bg-teal-700" data-testid="go-to-login-button">
                Aller à la connexion
              </Button>
              <Button variant="outline" onClick={handleResend} className="border-teal-200 text-teal-700 hover:bg-teal-50" data-testid="register-resend-button">
                Renvoyer l'e-mail de vérification
              </Button>
            </div>
            {resent && <p className="text-sm text-teal-700" data-testid="register-resend-message">{resent}</p>}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-start sm:items-center justify-center px-4 py-6 sm:py-8">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center gap-1 mb-4">
          <img src="/assets/logo-auth.png" alt="Mindset'iMMO" className="h-12 sm:h-14 w-auto" data-testid="auth-logo" />
          <p className="text-sm text-slate-500">Portail</p>
        </div>
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-xl" data-testid="register-title">Créer un compte</CardTitle>
            <CardDescription>Rejoins le portail et accède à tes applications.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 text-sm text-slate-700 mb-4" data-testid="skool-info">
              <p>Les applications Mindset'iMMO sont réservées aux membres de la communauté Skool de Dominique Piredda.</p>
              <p className="mt-1.5">Si tu n'es pas encore inscrit, clique sur le bouton orange ci-dessous.</p>
              <div className="flex justify-center pt-3">
                <a
                  href="https://www.skool.com/dominique-piredda"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium px-4 py-2 transition-colors"
                  data-testid="register-skool-link"
                >
                  Rejoindre la communauté Skool
                </a>
              </div>
              <p className="mt-3 font-semibold text-slate-900 bg-white border border-teal-300 rounded-md px-3 py-2 text-center" data-testid="skool-same-email-hint">
                Si tu es déjà membre, utilise la même adresse email pour bénéficier des applis Mindset'iMMO.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              {error && (
                <div className="bg-orange-50 border border-orange-200 text-orange-800 text-sm rounded-lg px-4 py-3" data-testid="register-error">
                  {error}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="prenom">Prénom</Label>
                  <Input id="prenom" required value={form.prenom} onChange={set("prenom")} data-testid="register-prenom-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nom">Nom</Label>
                  <Input id="nom" required value={form.nom} onChange={set("nom")} data-testid="register-nom-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={form.email} onChange={set("email")} placeholder="ton@email.fr" data-testid="register-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <Input id="password" type="password" required minLength={8} value={form.password} onChange={set("password")} placeholder="8 caractères minimum" data-testid="register-password-input" />
              </div>
              <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700" disabled={loading} data-testid="register-submit-button">
                {loading ? "Création..." : "Créer mon compte"}
              </Button>
            </form>
            <p className="mt-3 text-center text-sm text-slate-600">
              Déjà un compte ?{" "}
              <Link to="/connexion" className="text-teal-700 font-medium hover:underline" data-testid="login-link">
                Se connecter
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
